// BioForge 2.2 — visualization primitives: TechConvergence, RiskMatrix (scatter),
// VentureRadar, MarketFunnel, WorkflowGraph (xyflow), NumberCountUp.
import { useEffect, useMemo, useState, memo } from "react";
import { motion } from "framer-motion";
import {
  ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, ZAxis, Tooltip as RTooltip,
  CartesianGrid, ReferenceLine, ReferenceArea, Cell, RadarChart, PolarGrid, PolarAngleAxis,
  PolarRadiusAxis, Radar, BarChart, Bar,
} from "recharts";
import { ReactFlow, Background, Handle, Position } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { ArrowRight } from "lucide-react";
import { AGENTS } from "@/lib";

// ---------------------------------------------------------------------------
// Number count-up
// ---------------------------------------------------------------------------
export function NumberCountUp({ value, duration = 600, suffix = "", className = "" }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (value == null || isNaN(value)) return;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(value * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  if (value == null) return <span className={className}>—</span>;
  return <span className={className}>{n}{suffix}</span>;
}

// ---------------------------------------------------------------------------
// Technology Convergence — horizontal flow strip
// ---------------------------------------------------------------------------
export function TechConvergence({ problem, technologies = [], companyName, large = false }) {
  const techList = technologies.slice(0, 5);
  return (
    <div data-testid="tech-convergence"
      className={`relative rounded-xl border border-teal-400/15 bg-gradient-to-r from-teal-400/[0.04] via-white/[0.01] to-sky-400/[0.04] ${large ? "p-8" : "p-5"} overflow-hidden`}>
      <div className="absolute inset-0 dot-bg opacity-30 pointer-events-none" />
      <div className="relative flex items-stretch gap-5">
        <Pillar label="Problem" accent={false} className="flex-[1.1]">
          <div className={`${large ? "text-lg" : "text-sm"} font-semibold leading-snug`}>{problem || "—"}</div>
        </Pillar>
        <Connector />
        <Pillar label="Selected Technologies" accent className="flex-[1.6]">
          <div className="flex flex-wrap gap-1.5">
            {techList.length === 0 && <span className="text-xs text-white/40 italic">pending</span>}
            {techList.map((t, i) => (
              <motion.span key={t}
                initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08, duration: 0.22 }}
                className="text-[11px] px-2 py-0.5 rounded-md bg-teal-400/15 border border-teal-400/30 text-teal-100">
                {t}
              </motion.span>
            ))}
          </div>
        </Pillar>
        <Connector />
        <Pillar label="Generated Venture" accent={false} className="flex-1">
          <div className={`${large ? "text-2xl" : "text-base"} font-semibold tracking-tight text-teal-100`}>{companyName || "—"}</div>
        </Pillar>
      </div>
    </div>
  );
}
function Pillar({ label, accent, className = "", children }) {
  return (
    <div className={`min-w-0 ${className}`}>
      <div className={`text-[9px] uppercase tracking-[0.18em] mb-1.5 ${accent ? "text-teal-300/80" : "text-white/40"}`}>{label}</div>
      {children}
    </div>
  );
}
function Connector() {
  return (
    <div className="shrink-0 flex items-center text-teal-400/70">
      <ArrowRight className="w-5 h-5" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Risk Matrix — recharts ScatterChart with quadrant labels
// ---------------------------------------------------------------------------
const RISKS = [
  { key: "technical_risk", label: "Technical" },
  { key: "clinical_risk", label: "Clinical" },
  { key: "regulatory_risk", label: "Regulatory" },
  { key: "commercialization_risk", label: "Commercial" },
  { key: "scientific_risk", label: "Scientific" },
];
const SEV_IMPACT = { Low: 25, Medium: 55, High: 78, Critical: 92 };
const SEV_PROB   = { Low: 20, Medium: 45, High: 70, Critical: 88 };
const SEV_COLOR  = { Low: "#4ADE80", Medium: "#FBBF24", High: "#FB923C", Critical: "#F87171" };
const SEV_SIZE   = { Low: 200, Medium: 320, High: 460, Critical: 600 };

function hashJitter(name) {
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) % 1000;
  return ((h % 17) - 8);
}

export function RiskMatrix({ fr, onSelect }) {
  const data = useMemo(() => {
    if (!fr) return [];
    return RISKS.map((r) => {
      const risk = fr[r.key];
      if (!risk) return null;
      const sev = risk.severity || "Medium";
      const j1 = hashJitter(r.key);
      const j2 = hashJitter(r.key + "y");
      return {
        x: Math.min(96, Math.max(8, SEV_IMPACT[sev] + j1)),
        y: Math.min(96, Math.max(8, SEV_PROB[sev] + j2)),
        z: SEV_SIZE[sev] || 300,
        label: r.label,
        riskKey: r.key,
        severity: sev,
        explanation: risk.explanation,
        mitigation: risk.mitigation,
        fill: SEV_COLOR[sev] || "#9CA3AF",
      };
    }).filter(Boolean);
  }, [fr]);
  if (!fr) return <div className="text-sm text-white/40 italic">Founder review unavailable.</div>;

  return (
    <div data-testid="risk-matrix" className="relative rounded-xl border border-white/[0.06] bg-white/[0.015] p-4">
      <div className="absolute top-3 right-3 z-10 text-[9px] uppercase tracking-[0.18em] text-white/30">Impact × Probability</div>
      <div className="relative h-[420px] -ml-2">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 32, right: 28, bottom: 36, left: 28 }}>
            <CartesianGrid stroke="rgba(255,255,255,0.04)" />
            <ReferenceArea x1={50} x2={100} y1={50} y2={100} fill="#F87171" fillOpacity={0.04} />
            <ReferenceArea x1={0} x2={50} y1={50} y2={100} fill="#FBBF24" fillOpacity={0.03} />
            <ReferenceArea x1={50} x2={100} y1={0} y2={50} fill="#FB923C" fillOpacity={0.03} />
            <ReferenceArea x1={0} x2={50} y1={0} y2={50} fill="#4ADE80" fillOpacity={0.03} />
            <ReferenceLine x={50} stroke="rgba(255,255,255,0.12)" strokeDasharray="3 3" />
            <ReferenceLine y={50} stroke="rgba(255,255,255,0.12)" strokeDasharray="3 3" />
            <XAxis type="number" dataKey="x" domain={[0, 100]} tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }}
              label={{ value: "Impact →", position: "insideBottom", offset: -16, fill: "rgba(255,255,255,0.5)", fontSize: 11 }} />
            <YAxis type="number" dataKey="y" domain={[0, 100]} tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }}
              label={{ value: "Probability →", angle: -90, position: "insideLeft", fill: "rgba(255,255,255,0.5)", fontSize: 11 }} />
            <ZAxis type="number" dataKey="z" range={[120, 600]} />
            <RTooltip cursor={{ strokeDasharray: "3 3" }}
              contentStyle={{ background: "#0F1116", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }}
              labelStyle={{ color: "#5EEAD4" }}
              content={({ active, payload }) => {
                if (!active || !payload?.[0]) return null;
                const p = payload[0].payload;
                return (
                  <div className="rounded-md border border-white/10 bg-[#0F1116] p-3 max-w-xs shadow-xl">
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <div className="text-sm font-semibold text-white">{p.label}</div>
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: `${p.fill}22`, color: p.fill, border: `1px solid ${p.fill}55` }}>{p.severity}</span>
                    </div>
                    <div className="text-[11px] text-white/70 leading-snug line-clamp-3">{p.explanation}</div>
                    <div className="text-[10px] text-white/40 mt-2">Click for full mitigation →</div>
                  </div>
                );
              }} />
            <Scatter
              data={data}
              onClick={(d) => onSelect?.(d)}
              cursor="pointer">
              {data.map((entry, i) => (
                <Cell key={i} fill={entry.fill} fillOpacity={0.8} stroke={entry.fill} strokeWidth={1.5} />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
        <Quadrants />
      </div>
      <Legend data={data} onSelect={onSelect} />
      <div className="text-[9px] text-white/30 mt-2 text-center">Derived from Aignoz Innovation OS severity assessment · click any dot for full mitigation</div>
    </div>
  );
}

function Quadrants() {
  // Positioned to corners of the actual plot area (chart margins: t32, r28, b36, l28+Y-axis ~32).
  return (
    <>
      <div className="absolute z-10 pointer-events-none top-2 right-3 text-[10px] uppercase tracking-[0.16em] text-rose-300/70">Critical</div>
      <div className="absolute z-10 pointer-events-none top-2 left-16 text-[10px] uppercase tracking-[0.16em] text-amber-300/70">Monitor</div>
      <div className="absolute z-10 pointer-events-none bottom-[88px] right-3 text-[10px] uppercase tracking-[0.16em] text-orange-300/70">Strategic</div>
      <div className="absolute z-10 pointer-events-none bottom-[88px] left-16 text-[10px] uppercase tracking-[0.16em] text-emerald-300/70">Minor</div>
    </>
  );
}

function Legend({ data, onSelect }) {
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {data.map((d) => (
        <button key={d.label} data-testid={`risk-legend-${d.riskKey}`}
          onClick={() => onSelect?.(d)}
          className="inline-flex items-center gap-1.5 text-[10px] px-2 py-0.5 rounded-md border hover:scale-105 transition"
          style={{ background: `${d.fill}12`, color: d.fill, borderColor: `${d.fill}40` }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: d.fill }} />{d.label}: {d.severity}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Venture Radar — 8-axis radar chart
// ---------------------------------------------------------------------------
const RADAR_AXES = [
  ["innovation_score", "Innovation", false],
  ["feasibility_score", "Feasibility", false],
  ["regulatory_readiness_score", "Regulatory", false],
  ["clinical_adoption_score", "Clinical", false],
  ["investor_attractiveness_score", "Investor", false],
  ["scientific_novelty_score", "Novelty", false],
  ["time_to_market_score", "TTM", false],
  ["technical_complexity_score", "Simplicity", true], // inverted: lower complexity = better
];

export function VentureRadar({ scores, highlight = null, onHover, large = false }) {
  const data = useMemo(() => RADAR_AXES.map(([k, label, inv]) => {
    const s = scores?.[k]?.score;
    const v = s == null ? 0 : (inv ? 100 - s : s);
    return { axis: label, value: v, key: k };
  }), [scores]);
  const overall = useMemo(() => {
    const vals = data.map((d) => d.value).filter((v) => v > 0);
    return vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : 0;
  }, [data]);

  return (
    <div className="relative w-full" data-testid="venture-radar">
      <div style={{ height: large ? 480 : 360 }}>
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={data} cx="50%" cy="50%" outerRadius={large ? "78%" : "75%"}>
            <PolarGrid stroke="rgba(255,255,255,0.08)" />
            <PolarAngleAxis dataKey="axis" tick={({ payload, x, y, textAnchor }) => (
              <text x={x} y={y} textAnchor={textAnchor} fill={highlight && highlight !== payload.value ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.7)"} fontSize={11}>
                {payload.value}
              </text>
            )} />
            <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: "rgba(255,255,255,0.2)", fontSize: 9 }} stroke="rgba(255,255,255,0.06)" />
            <Radar dataKey="value" stroke="#5EEAD4" strokeWidth={2} fill="#5EEAD4" fillOpacity={0.18} />
          </RadarChart>
        </ResponsiveContainer>
      </div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none text-center">
        <div className="text-[9px] uppercase tracking-[0.18em] text-white/40 mb-0.5">Overall</div>
        <div className="text-3xl font-semibold text-teal-200"><NumberCountUp value={overall} /></div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Market funnel — SVG TAM/SAM/SOM pyramid
// ---------------------------------------------------------------------------
export function MarketFunnel({ tam, sam, som, large = false }) {
  const fmt = (n) => {
    if (n == null) return "—";
    if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
    if (n >= 1e6) return `$${(n / 1e6).toFixed(0)}M`;
    return `$${Math.round(n)}`;
  };
  const fmtINR = (n) => {
    if (n == null) return "";
    const inr = n * 83;
    if (inr >= 1e10) return `₹${(inr / 1e10).toFixed(2)}K Cr`;
    if (inr >= 1e7)  return `₹${(inr / 1e7).toFixed(inr >= 1e8 ? 0 : 1)} Cr`;
    if (inr >= 1e5)  return `₹${(inr / 1e5).toFixed(1)} L`;
    if (inr >= 1e3)  return `₹${Math.round(inr).toLocaleString("en-IN")}`;
    return `₹${Math.round(inr)}`;
  };
  const W = 400, H = large ? 280 : 200;
  const rows = [
    { label: "TAM", value: tam, fill: "rgba(94,234,212,0.18)", stroke: "rgba(94,234,212,0.55)", w: 1.0 },
    { label: "SAM", value: sam, fill: "rgba(125,211,252,0.22)", stroke: "rgba(125,211,252,0.6)", w: 0.72 },
    { label: "SOM", value: som, fill: "rgba(165,243,252,0.28)", stroke: "rgba(165,243,252,0.7)", w: 0.42 },
  ];
  const segH = H / 3;
  return (
    <div data-testid="market-funnel" className="relative w-full" style={{ maxWidth: W }}>
      <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`}>
        {rows.map((r, i) => {
          const inset = ((1 - r.w) / 2) * W;
          return (
            <g key={r.label}>
              <rect x={inset} y={i * segH + 4} width={W - 2 * inset} height={segH - 8} rx={4}
                fill={r.fill} stroke={r.stroke} strokeWidth={1} />
              <text x={W / 2} y={i * segH + segH / 2 - 8} textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize={9} style={{ letterSpacing: "0.18em" }}>{r.label}</text>
              <text x={W / 2} y={i * segH + segH / 2 + 8} textAnchor="middle" fill="#E6F8F4" fontSize={large ? 22 : 18} fontWeight={600} fontFamily="JetBrains Mono">{fmt(r.value)}</text>
              {r.value != null && (
                <text x={W / 2} y={i * segH + segH / 2 + (large ? 28 : 24)} textAnchor="middle" fill="rgba(230,248,244,0.5)" fontSize={large ? 12 : 11} fontFamily="JetBrains Mono">{fmtINR(r.value)}</text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Technology trends bar chart (across runs)
// ---------------------------------------------------------------------------
export function TechTrendsBar({ data }) {
  return (
    <div className="h-44 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
          <XAxis type="number" hide />
          <YAxis type="category" dataKey="tech" tick={{ fill: "rgba(255,255,255,0.6)", fontSize: 11 }} width={130} axisLine={false} tickLine={false} />
          <Bar dataKey="count" fill="#5EEAD4" radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Workflow Graph — @xyflow/react, tree layout
// ---------------------------------------------------------------------------
const AgentNode = memo(({ data }) => {
  const status = data.status; // 'complete' | 'running' | 'idle' | 'failed'
  const ring = status === "complete" ? "border-emerald-400/50 bg-emerald-400/5"
    : status === "running" ? "border-teal-400/60 bg-teal-400/8 node-pulse"
    : status === "failed" ? "border-rose-400/60 bg-rose-400/8"
    : "border-white/10 bg-white/[0.02]";
  return (
    <div className={`relative rounded-lg border ${ring} px-3 py-2 min-w-[170px] backdrop-blur transition cursor-pointer`}
      data-testid={`graph-node-${data.event}`}>
      <Handle type="target" position={Position.Top} />
      <div className="flex items-center gap-2 mb-1">
        <span className={`w-1.5 h-1.5 rounded-full ${status === "complete" ? "bg-emerald-300" : status === "running" ? "bg-teal-300 pulse-dot" : status === "failed" ? "bg-rose-300" : "bg-white/30"}`} />
        <div className="text-[10px] uppercase tracking-[0.14em] text-white/40 truncate">{data.short}</div>
      </div>
      <div className="text-[12.5px] font-semibold text-white/90 truncate">{data.cls}</div>
      <div className="flex items-center gap-2 mt-1">
        {data.confidence != null && (
          <span className="text-[10px] mono px-1 py-0.5 rounded bg-white/[0.06] text-white/70">c{data.confidence}</span>
        )}
        {data.citations != null && data.citations > 0 && (
          <span className="text-[10px] mono text-teal-300/80">{data.citations}↩</span>
        )}
        {data.duration != null && (
          <span className="text-[10px] mono text-white/40 ml-auto">{(data.duration / 1000).toFixed(1)}s</span>
        )}
      </div>
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
});

const NODE_TYPES = { agent: AgentNode };

export function WorkflowGraph({ doc, stream, onNodeClick }) {
  const { nodes, edges } = useMemo(() => buildGraph(doc, stream), [doc, stream]);
  return (
    <div className="w-full h-full" data-testid="workflow-graph">
      <ReactFlow
        nodes={nodes} edges={edges}
        nodeTypes={NODE_TYPES}
        onNodeClick={(_, n) => onNodeClick?.(n.data)}
        proOptions={{ hideAttribution: true }}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.3} maxZoom={1.5}
        panOnDrag={true}
      >
        <Background color="rgba(255,255,255,0.06)" gap={20} size={1} />
      </ReactFlow>
    </div>
  );
}

function buildGraph(doc, stream) {
  // Tree layout. Levels:
  // L0: problem_analyst
  // L1: research_scientist, technology_matchmaker
  // L2: innovation_architect
  // L3: prototype_designer
  // L4: validation_planner, regulatory_planner, clinical_trial_designer
  // L5: commercialization_agent
  // L6: founder_review
  // L7: scorer
  // L8: final_report_compiler
  const layout = {
    problem_analyst:        { x: 400, y: 0 },
    research_scientist:     { x: 200, y: 110 },
    technology_matchmaker:  { x: 600, y: 110 },
    innovation_architect:   { x: 400, y: 220 },
    prototype_designer:     { x: 400, y: 330 },
    validation_planner:     { x: 120, y: 440 },
    regulatory_planner:     { x: 400, y: 440 },
    clinical_trial_designer:{ x: 680, y: 440 },
    commercialization_agent:{ x: 400, y: 550 },
    founder_review:         { x: 400, y: 660 },
    scorer:                 { x: 400, y: 770 },
    final_report_compiler:  { x: 400, y: 880 },
  };
  const edgesList = [
    ["problem_analyst", "research_scientist"],
    ["problem_analyst", "technology_matchmaker"],
    ["research_scientist", "innovation_architect"],
    ["technology_matchmaker", "innovation_architect"],
    ["innovation_architect", "prototype_designer"],
    ["prototype_designer", "validation_planner"],
    ["prototype_designer", "regulatory_planner"],
    ["prototype_designer", "clinical_trial_designer"],
    ["validation_planner", "commercialization_agent"],
    ["regulatory_planner", "commercialization_agent"],
    ["clinical_trial_designer", "commercialization_agent"],
    ["commercialization_agent", "founder_review"],
    ["founder_review", "scorer"],
    ["scorer", "final_report_compiler"],
  ];
  const agentStream = stream?.agents || {};
  const outputs = doc?.outputs || {};
  const traceByAgent = {};
  for (const e of (doc?.trace || [])) {
    if (e.agent && (e.event === "agent_complete" || e.event === "agent_failed")) {
      traceByAgent[e.agent] = { duration: e.duration_ms, retries: e.retry_count };
    }
  }
  const nodes = AGENTS.map((a) => {
    const live = agentStream[a.event];
    const out = outputs[a.event];
    const status = live?.completed || out ? "complete"
      : live?.started ? "running"
      : "idle";
    const conf = live?.confidence ?? out?.confidence_score;
    const citations = (out?.citations || []).length;
    const pos = layout[a.event] || { x: 0, y: 0 };
    return {
      id: a.event,
      type: "agent",
      position: pos,
      data: {
        event: a.event,
        short: a.short,
        cls: a.cls,
        desc: a.desc,
        status,
        confidence: conf,
        citations,
        duration: traceByAgent[a.event]?.duration,
        output: out || live?.output,
      },
    };
  });
  const edges = edgesList.map(([s, t]) => {
    const sActive = (agentStream[s]?.completed || outputs[s]);
    const tActive = (agentStream[t]?.started && !agentStream[t]?.completed);
    return {
      id: `${s}->${t}`,
      source: s, target: t,
      animated: sActive && tActive,
      style: { strokeWidth: 1.5, stroke: sActive ? "rgba(94,234,212,0.5)" : "rgba(255,255,255,0.1)" },
    };
  });
  return { nodes, edges };
}
